import React, { useState, useEffect, useMemo } from 'react';
import { apiUrl } from '../api';

const NearExpiry = () => {
  const [expiringItems, setExpiringItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);

  // Pop Card Modal States
  const [popupType, setPopupType] = useState(null); // 'expired' | 'expiring_soon' | null
  const [popupSearch, setPopupSearch] = useState('');
  const [popupSupplierFilter, setPopupSupplierFilter] = useState('ALL');

  // Main Page Filter & Search States
  const [mainFilter, setMainFilter] = useState('ALL'); // 'ALL' | 'EXPIRED' | 'SOON'
  const [mainSearch, setMainSearch] = useState('');

  // Party Modal Filter State
  const [partyItemsFilter, setPartyItemsFilter] = useState('ALL'); // 'ALL' | 'EXPIRED' | 'SOON'

  const fetchPurchases = async () => {
    try {
      setIsLoading(true);
      const [purRes, cnRes] = await Promise.all([
        fetch(apiUrl('/api/purchases')),
        fetch(apiUrl('/api/credit-notes')).catch(() => ({ ok: false }))
      ]);

      if (!purRes.ok) {
        throw new Error('Failed to fetch data');
      }
      const purchases = await purRes.json();
      let creditNotes = [];
      if (cnRes && cnRes.ok) {
        creditNotes = await cnRes.json();
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const twoMonthsLater = new Date(today);
      twoMonthsLater.setMonth(today.getMonth() + 2);
      // End of that month
      const twoMonthsLaterEnd = new Date(twoMonthsLater.getFullYear(), twoMonthsLater.getMonth() + 1, 0, 23, 59, 59);

      const items = [];

      purchases.forEach(purchase => {
        if (purchase.items && Array.isArray(purchase.items)) {
          purchase.items.forEach(item => {
            if (item.exp) {
              const parts = item.exp.split('/');
              if (parts.length === 2) {
                const month = parseInt(parts[0], 10) - 1; // 0-indexed
                let year = parseInt(parts[1], 10);
                if (year < 100) year += 2000;
                
                // Expiry date is the last day of the given month at 23:59:59
                const expDate = new Date(year, month + 1, 0, 23, 59, 59);

                // If expiry date is before or equal to two months from now
                if (expDate <= twoMonthsLaterEnd) {
                  const billed = parseInt(item.qty, 10) || 0;
                  const free = parseInt(item.freeQty || item.sch || item.free || item.scheme, 10) || 0;
                  const qty = billed + free;
                  
                  // Calculate quantity returned to supplier via Credit Notes
                  let returnedQty = 0;
                  (creditNotes || []).forEach(cn => {
                    const sameSupplier = !cn.supplierName || !purchase.supplierName ||
                      cn.supplierName.trim().toUpperCase() === purchase.supplierName.trim().toUpperCase();
                    if (sameSupplier && cn.items && Array.isArray(cn.items)) {
                      cn.items.forEach(cnItem => {
                        const sameProduct = cnItem.productName && item.productName &&
                          cnItem.productName.trim().toUpperCase() === item.productName.trim().toUpperCase();
                        const sameBatch = !cnItem.batch || !item.batch ||
                          cnItem.batch.trim().toUpperCase() === item.batch.trim().toUpperCase();
                        if (sameProduct && sameBatch) {
                          returnedQty += (parseFloat(cnItem.qty) || 0);
                        }
                      });
                    }
                  });

                  const remainingQty = Math.max(0, qty - returnedQty);

                  // Only include if remaining quantity is greater than 0
                  if (remainingQty > 0) {
                    const rate = parseFloat(item.rate || item.mrp || 0) || 0;
                    const amount = remainingQty * rate;
                    const isExp = expDate < today;
                    const diffTime = expDate.getTime() - today.getTime();
                    const daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    items.push({
                      id: `${purchase.id}-${item.productName}-${item.batch}`,
                      purchaseId: purchase.id,
                      invoiceNo: purchase.invoiceNo || purchase.vchNo || 'N/A',
                      purchaseDate: purchase.date || '-',
                      productName: item.productName,
                      company: item.mfg || item.company || '-',
                      supplier: purchase.supplierName || 'Unknown',
                      exp: item.exp,
                      qty: remainingQty,
                      rate: rate,
                      amount: amount,
                      batch: item.batch || '-',
                      isExpired: isExp,
                      daysDiff: daysDiff,
                      dateObj: expDate
                    });
                  }
                }
              }
            }
          });
        }
      });

      // Sort items by expiry date (oldest/expired first)
      items.sort((a, b) => a.dateObj - b.dateObj);
      
      setExpiringItems(items);
    } catch (err) {
      console.error("Error fetching purchases:", err);
      setError("Failed to load near expiry data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  // Listen to Escape key to close modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (popupType) setPopupType(null);
        else if (selectedParty) setSelectedParty(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [popupType, selectedParty]);

  // Derived item collections
  const expiredItems = useMemo(() => expiringItems.filter(item => item.isExpired), [expiringItems]);
  const expiringSoonItems = useMemo(() => expiringItems.filter(item => !item.isExpired), [expiringItems]);

  const expiredTotalQty = useMemo(() => expiredItems.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0), [expiredItems]);
  const expiredTotalValue = useMemo(() => expiredItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0), [expiredItems]);

  const expiringSoonTotalQty = useMemo(() => expiringSoonItems.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0), [expiringSoonItems]);
  const expiringSoonTotalValue = useMemo(() => expiringSoonItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0), [expiringSoonItems]);

  // Pop Card Active Items (filtered by search and supplier)
  const activePopupItems = useMemo(() => {
    if (!popupType) return [];
    const baseList = popupType === 'expired' ? expiredItems : expiringSoonItems;
    return baseList.filter(item => {
      if (popupSupplierFilter !== 'ALL' && item.supplier !== popupSupplierFilter) {
        return false;
      }
      if (popupSearch.trim()) {
        const q = popupSearch.toLowerCase();
        const matchName = (item.productName || '').toLowerCase().includes(q);
        const matchComp = (item.company || '').toLowerCase().includes(q);
        const matchBatch = (item.batch || '').toLowerCase().includes(q);
        const matchInv = (item.invoiceNo || '').toLowerCase().includes(q);
        const matchSupp = (item.supplier || '').toLowerCase().includes(q);
        return matchName || matchComp || matchBatch || matchInv || matchSupp;
      }
      return true;
    });
  }, [popupType, expiredItems, expiringSoonItems, popupSupplierFilter, popupSearch]);

  const popupTotalQty = useMemo(() => activePopupItems.reduce((sum, it) => sum + (parseInt(it.qty) || 0), 0), [activePopupItems]);
  const popupTotalValue = useMemo(() => activePopupItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0), [activePopupItems]);
  const popupSuppliers = useMemo(() => Array.from(new Set(activePopupItems.map(it => it.supplier))), [activePopupItems]);

  const currentCategorySuppliers = useMemo(() => {
    const list = popupType === 'expired' ? expiredItems : expiringSoonItems;
    return Array.from(new Set(list.map(it => it.supplier))).sort();
  }, [popupType, expiredItems, expiringSoonItems]);

  // Main Page Parties calculation
  const allParties = useMemo(() => Array.from(new Set(expiringItems.map(item => item.supplier))), [expiringItems]);

  const filteredParties = useMemo(() => {
    return allParties.filter(party => {
      const partyItems = expiringItems.filter(item => item.supplier === party);
      const hasExpired = partyItems.some(item => item.isExpired);
      const hasSoon = partyItems.some(item => !item.isExpired);

      if (mainFilter === 'EXPIRED' && !hasExpired) return false;
      if (mainFilter === 'SOON' && !hasSoon) return false;

      if (mainSearch.trim()) {
        const q = mainSearch.toLowerCase();
        const matchParty = party.toLowerCase().includes(q);
        const matchMedicines = partyItems.some(it => 
          (it.productName || '').toLowerCase().includes(q) ||
          (it.batch || '').toLowerCase().includes(q) ||
          (it.invoiceNo || '').toLowerCase().includes(q)
        );
        return matchParty || matchMedicines;
      }
      return true;
    });
  }, [allParties, expiringItems, mainFilter, mainSearch]);

  // Copy list for individual party
  const handleCopyReturnList = (party, partyItems) => {
    const totalQty = partyItems.reduce((sum, item) => sum + item.qty, 0);
    const totalAmt = partyItems.reduce((sum, item) => sum + item.amount, 0);
    
    let text = `📦 *STOCK RETURN NOTICE - ${party.toUpperCase()}*\n`;
    text += `Date: ${new Date().toLocaleDateString('en-IN')}\n`;
    text += `-----------------------------------\n`;
    partyItems.forEach((item, i) => {
      text += `${i + 1}. ${item.productName} (${item.company})\n`;
      text += `   Bill No: ${item.invoiceNo} | Date: ${item.purchaseDate}\n`;
      text += `   Batch: ${item.batch} | Exp: ${item.exp} ${item.isExpired ? '⚠️ [EXPIRED]' : '[EXPIRING SOON]'}\n`;
      text += `   Qty: ${item.qty} | Rate: ₹${item.rate} | Return Value: ₹${item.amount.toFixed(2)}\n\n`;
    });
    text += `-----------------------------------\n`;
    text += `Total Items: ${partyItems.length} | Total Qty: ${totalQty}\n`;
    text += `Total Stock Return Amount: ₹${totalAmt.toFixed(2)}\n`;
    
    navigator.clipboard.writeText(text);
    alert(`Stock Return Details for ${party} copied to clipboard!`);
  };

  // Print note for individual party
  const handlePrintReturnNote = (party, partyItems) => {
    const totalQty = partyItems.reduce((sum, item) => sum + item.qty, 0);
    const totalAmt = partyItems.reduce((sum, item) => sum + item.amount, 0);
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Stock Return - ${party}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; }
            h2 { margin-bottom: 5px; color: #0f172a; }
            .header { margin-bottom: 20px; border-bottom: 2px solid #cbd5e1; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left; font-size: 13px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            .right { text-align: right; }
            .center { text-align: center; }
            .badge-expired { background: #ffe4e6; color: #be123c; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 11px; }
            .badge-soon { background: #fef3c7; color: #b45309; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 11px; }
            .footer { margin-top: 20px; text-align: right; font-weight: bold; font-size: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>STOCK RETURN MEMO</h2>
            <div><strong>Supplier / Stockist Party:</strong> ${party}</div>
            <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-IN')}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Bill No</th>
                <th>Bill Date</th>
                <th>Product Name</th>
                <th>Batch</th>
                <th>Exp Date</th>
                <th class="center">Qty</th>
                <th class="right">Rate (₹)</th>
                <th class="right">Return Amt (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${partyItems.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.invoiceNo}</td>
                  <td>${item.purchaseDate}</td>
                  <td><strong>${item.productName}</strong><br><small>${item.company}</small></td>
                  <td>${item.batch}</td>
                  <td><span class="${item.isExpired ? 'badge-expired' : 'badge-soon'}">${item.exp} ${item.isExpired ? '(Expired)' : ''}</span></td>
                  <td class="center"><strong>${item.qty}</strong></td>
                  <td class="right">${item.rate.toFixed(2)}</td>
                  <td class="right"><strong>${item.amount.toFixed(2)}</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            <div>Total Return Quantity: ${totalQty}</div>
            <div style="color: #0d9488; font-size: 18px; margin-top: 5px;">Total Stock Return Value: ₹${totalAmt.toFixed(2)}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Pop Card Print Report
  const handlePrintPopupReport = (type, items) => {
    if (!items || items.length === 0) return;
    const isExp = type === 'expired';
    const totalQty = items.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0);
    const totalAmt = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const title = isExp ? 'EXPIRED STOCK REPORT' : 'EXPIRING SOON STOCK REPORT (NEXT 2 MONTHS)';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1e293b; }
            h2 { margin: 0 0 4px 0; color: ${isExp ? '#be123c' : '#b45309'}; font-size: 20px; }
            .sub { color: #64748b; font-size: 12px; margin-bottom: 16px; font-weight: bold; }
            .header-bar { border-bottom: 2px solid #cbd5e1; padding-bottom: 12px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-end; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #cbd5e1; padding: 7px 10px; text-align: left; font-size: 12px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            .right { text-align: right; }
            .center { text-align: center; }
            .badge-expired { background: #ffe4e6; color: #be123c; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 11px; }
            .badge-soon { background: #fef3c7; color: #b45309; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 11px; }
            .footer { margin-top: 20px; border-top: 2px solid #cbd5e1; padding-top: 12px; display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header-bar">
            <div>
              <h2>${title}</h2>
              <div class="sub">Generated on: ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}</div>
            </div>
            <div style="text-align: right; font-size: 12px;">
              <div>Total Products: <strong>${items.length}</strong></div>
              <div>Total Units: <strong>${totalQty}</strong></div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Name</th>
                <th>Supplier / Stockist</th>
                <th>Bill No & Date</th>
                <th>Batch</th>
                <th class="center">Exp</th>
                <th class="center">Qty</th>
                <th class="right">Rate (₹)</th>
                <th class="right">Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${items.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td><strong>${item.productName}</strong><br><small style="color:#64748b;">${item.company}</small></td>
                  <td>${item.supplier}</td>
                  <td>#${item.invoiceNo} <small>(${item.purchaseDate})</small></td>
                  <td style="font-family: monospace; font-weight: bold;">${item.batch}</td>
                  <td class="center"><span class="${item.isExpired ? 'badge-expired' : 'badge-soon'}">${item.exp} ${item.isExpired ? '(Expired)' : ''}</span></td>
                  <td class="center"><strong>${item.qty}</strong></td>
                  <td class="right">${item.rate.toFixed(2)}</td>
                  <td class="right"><strong>${item.amount.toFixed(2)}</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            <div>Total Return Quantity: ${totalQty} units across ${items.length} medicines</div>
            <div style="color: ${isExp ? '#be123c' : '#0d9488'}; font-size: 16px;">Total Value: ₹${totalAmt.toFixed(2)}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Pop Card Copy to Clipboard
  const handleCopyPopupReport = (type, items) => {
    if (!items || items.length === 0) return;
    const isExp = type === 'expired';
    const totalQty = items.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0);
    const totalAmt = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const title = isExp ? 'EXPIRED STOCK REPORT' : 'EXPIRING SOON STOCK REPORT';

    let text = `📦 *${title}*\n`;
    text += `Date: ${new Date().toLocaleDateString('en-IN')}\n`;
    text += `-----------------------------------\n`;
    items.forEach((item, i) => {
      text += `${i + 1}. ${item.productName} (${item.company})\n`;
      text += `   Supplier: ${item.supplier} | Bill: #${item.invoiceNo} (${item.purchaseDate})\n`;
      text += `   Batch: ${item.batch} | Exp: ${item.exp} ${item.isExpired ? '⚠️ [EXPIRED]' : '⏳ [EXPIRING SOON]'}\n`;
      text += `   Qty: ${item.qty} | Rate: ₹${item.rate} | Total: ₹${item.amount.toFixed(2)}\n\n`;
    });
    text += `-----------------------------------\n`;
    text += `Total Products: ${items.length} | Total Qty: ${totalQty}\n`;
    text += `Total Return Value: ₹${totalAmt.toFixed(2)}\n`;

    navigator.clipboard.writeText(text);
    alert(`${title} (${items.length} items) copied to clipboard!`);
  };

  const [isCreatingCN, setIsCreatingCN] = useState(false);
  const [showCNSuccessModal, setShowCNSuccessModal] = useState(null);
  const [customCnAmount, setCustomCnAmount] = useState('');

  const handleCreateCreditNote = async (party, partyItems, overrideAmount) => {
    if (!partyItems || partyItems.length === 0) return;
    const totalQty = partyItems.reduce((sum, item) => sum + item.qty, 0);
    const calculatedAmt = partyItems.reduce((sum, item) => sum + item.amount, 0);
    const totalAmt = overrideAmount !== undefined && overrideAmount !== '' ? (parseFloat(overrideAmount) || 0) : calculatedAmt;
    const cnNo = `CN-${Math.floor(1000 + Math.random() * 9000)}`;
    const dateStr = new Date().toISOString().split('T')[0];

    const creditNoteData = {
      creditNoteNo: cnNo,
      supplierName: party,
      date: dateStr,
      totalAmount: totalAmt,
      totalQty: totalQty,
      totalItems: partyItems.length,
      items: partyItems.map(it => ({
        productName: it.productName,
        company: it.company,
        batch: it.batch,
        exp: it.exp,
        qty: it.qty,
        rate: it.rate,
        amount: it.amount,
        invoiceNo: it.invoiceNo
      })),
      reason: 'Expiry Stock Return to Party'
    };

    try {
      setIsCreatingCN(true);
      let savedData = null;
      try {
        const res = await fetch(apiUrl('/api/credit-notes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(creditNoteData)
        });
        if (res.ok) {
          savedData = await res.json();
        }
      } catch (e) {
        console.error('Error creating credit note:', e);
      }

      setShowCNSuccessModal(savedData || creditNoteData);
      fetchPurchases();
    } catch (err) {
      console.error('Error creating credit note:', err);
      alert('Failed to generate Credit Note: ' + err.message);
    } finally {
      setIsCreatingCN(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 relative">
      <div className="px-4 py-6 md:px-8 max-w-[90rem] mx-auto w-full h-full flex flex-col">
        {/* Header Bar */}
        <div className="mb-6 shrink-0 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight">Near Expiry & Stock Return</h2>
            <p className="text-sm font-bold text-slate-500 mt-1">Party-wise summary of expired & near-expiry products for stock return to suppliers</p>
          </div>
          
          {/* Clickable Expired & Expiring Soon Button Cards */}
          <div className="flex items-center gap-3 flex-wrap">
             {/* Expired Button (Clickable -> Opens Expired Pop Card) */}
             <button
               type="button"
               onClick={() => {
                 setPopupType('expired');
                 setPopupSearch('');
                 setPopupSupplierFilter('ALL');
               }}
               className="group flex items-center gap-3 bg-white hover:bg-rose-50/80 active:bg-rose-100 border-2 border-rose-200 hover:border-rose-400 px-4 py-2.5 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer text-left active:scale-95"
               title="Click to pop card and view all expired medicines"
             >
                <div className="flex items-center gap-2.5">
                   <div className="relative flex items-center justify-center">
                     <span className="w-3.5 h-3.5 rounded-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.7)] animate-pulse"></span>
                   </div>
                   <div>
                     <div className="flex items-center gap-2">
                       <span className="text-xs font-black text-slate-800 group-hover:text-rose-700 transition-colors">Expired</span>
                       <span className="px-2 py-0.5 bg-rose-100 text-rose-800 text-[11px] font-black rounded-md border border-rose-200">
                         {expiredItems.length} {expiredItems.length === 1 ? 'item' : 'items'}
                       </span>
                     </div>
                     <div className="text-[10px] font-bold text-slate-400 group-hover:text-rose-600 transition-colors flex items-center gap-1 mt-0.5">
                       <span>₹{expiredTotalValue.toFixed(2)} ({expiredTotalQty} units)</span>
                       <span>•</span>
                       <span className="underline decoration-dotted underline-offset-2">Click to pop card</span>
                     </div>
                   </div>
                </div>
                <div className="w-6 h-6 rounded-lg bg-rose-50 group-hover:bg-rose-600 text-rose-500 group-hover:text-white flex items-center justify-center transition-all ml-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
             </button>

             {/* Expiring Soon Button (Clickable -> Opens Expiring Soon Pop Card) */}
             <button
               type="button"
               onClick={() => {
                 setPopupType('expiring_soon');
                 setPopupSearch('');
                 setPopupSupplierFilter('ALL');
               }}
               className="group flex items-center gap-3 bg-white hover:bg-amber-50/80 active:bg-amber-100 border-2 border-amber-200 hover:border-amber-400 px-4 py-2.5 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer text-left active:scale-95"
               title="Click to pop card and view all items expiring in the next 2 months"
             >
                <div className="flex items-center gap-2.5">
                   <div className="relative flex items-center justify-center">
                     <span className="w-3.5 h-3.5 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.7)]"></span>
                   </div>
                   <div>
                     <div className="flex items-center gap-2">
                       <span className="text-xs font-black text-slate-800 group-hover:text-amber-700 transition-colors">Expiring Soon</span>
                       <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[11px] font-black rounded-md border border-amber-200">
                         {expiringSoonItems.length} {expiringSoonItems.length === 1 ? 'item' : 'items'}
                       </span>
                     </div>
                     <div className="text-[10px] font-bold text-slate-400 group-hover:text-amber-600 transition-colors flex items-center gap-1 mt-0.5">
                       <span>₹{expiringSoonTotalValue.toFixed(2)} ({expiringSoonTotalQty} units)</span>
                       <span>•</span>
                       <span className="underline decoration-dotted underline-offset-2">Click to pop card</span>
                     </div>
                   </div>
                </div>
                <div className="w-6 h-6 rounded-lg bg-amber-50 group-hover:bg-amber-600 text-amber-600 group-hover:text-white flex items-center justify-center transition-all ml-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
             </button>
          </div>
        </div>

        {/* Filter Pills & Search for Main Table */}
        <div className="mb-4 shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => setMainFilter('ALL')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                mainFilter === 'ALL'
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              All Parties ({allParties.length})
            </button>
            <button
              onClick={() => setMainFilter('EXPIRED')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 ${
                mainFilter === 'EXPIRED'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-white hover:bg-rose-50 text-rose-700 border border-rose-200'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              With Expired ({allParties.filter(p => expiringItems.filter(i => i.supplier === p).some(i => i.isExpired)).length})
            </button>
            <button
              onClick={() => setMainFilter('SOON')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 ${
                mainFilter === 'SOON'
                  ? 'bg-amber-600 text-white shadow-sm'
                  : 'bg-white hover:bg-amber-50 text-amber-800 border border-amber-200'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              With Near Expiry ({allParties.filter(p => expiringItems.filter(i => i.supplier === p).some(i => !i.isExpired)).length})
            </button>
          </div>

          <div className="relative w-full sm:w-72">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={mainSearch}
              onChange={(e) => setMainSearch(e.target.value)}
              placeholder="Filter party, bill, or product..."
              className="w-full pl-8 pr-7 py-1.5 text-xs font-bold bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-slate-700 placeholder:text-slate-400"
            />
            {mainSearch && (
              <button
                onClick={() => setMainSearch('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
               <svg className="w-10 h-10 text-emerald-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
               </svg>
               <p className="mt-4 font-bold text-slate-500 animate-pulse">Checking records...</p>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <svg className="w-12 h-12 text-rose-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-slate-600 font-bold">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold transition-colors text-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : expiringItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-slate-800 font-black text-xl mb-1">All Clear!</p>
                <p className="text-slate-500 font-medium">No products are expiring within the next 2 months.</p>
              </div>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center text-slate-400">
              <div>
                <p className="font-bold text-slate-700 text-sm">No supplier parties match the selected filter</p>
                <button
                  onClick={() => { setMainFilter('ALL'); setMainSearch(''); }}
                  className="mt-3 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm">
                  <tr>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60">Supplier Party</th>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60">Bills / Invoices</th>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60 text-center">Items Count</th>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60 text-center">Return Qty</th>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60 text-right">Return Value (₹)</th>
                    <th className="py-4 px-6 font-black text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200/60 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParties.map((party, index) => {
                    const partyItems = expiringItems.filter(item => item.supplier === party);
                    const totalQty = partyItems.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0);
                    const totalValue = partyItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                    const hasExpired = partyItems.some(item => item.isExpired);
                    const uniqueBills = Array.from(new Set(partyItems.map(item => item.invoiceNo))).filter(b => b && b !== 'N/A');
                    
                    return (
                      <tr 
                        key={party + index} 
                        onClick={() => setSelectedParty(party)}
                        className={`cursor-pointer hover:bg-slate-50/80 transition-colors border-b border-slate-100 last:border-0 ${hasExpired ? 'bg-rose-50/30 hover:bg-rose-100/50' : 'hover:bg-slate-100/80'}`}
                      >
                        <td className="py-4 px-6">
                          <div className="font-bold text-blue-600 group-hover:text-blue-800 text-base">{party}</div>
                          {hasExpired && <span className="inline-block mt-1 px-2 py-0.5 bg-rose-100 text-rose-700 font-bold text-[10px] rounded-full">Contains Expired Items</span>}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-wrap gap-1">
                            {uniqueBills.length > 0 ? (
                              uniqueBills.map((bNo, bIdx) => (
                                <span key={bIdx} className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 font-bold text-xs rounded">
                                  #{bNo}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-400 text-xs font-medium">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center font-bold text-slate-700">
                          {partyItems.length} products
                        </td>
                        <td className="py-4 px-6 text-center font-black text-slate-700">
                          {totalQty}
                        </td>
                        <td className="py-4 px-6 text-right font-black text-emerald-600 text-base">
                          ₹{totalValue.toFixed(2)}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedParty(party);
                            }}
                            className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-lg transition-colors border border-indigo-200"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* POP CARD MODAL: Expired / Expiring Soon Detailed Card */}
      {popupType && (() => {
        const isExp = popupType === 'expired';
        return (
          <div 
            onClick={() => setPopupType(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[92vh] border border-slate-200 animate-in zoom-in-95 duration-200"
            >
              {/* Header */}
              <div className={`flex items-center justify-between px-6 py-4.5 text-white ${
                isExp ? 'bg-gradient-to-r from-rose-600 via-rose-700 to-red-800' : 'bg-gradient-to-r from-amber-600 via-amber-700 to-orange-700'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-xl backdrop-blur-xs shadow-inner">
                    {isExp ? '🛑' : '⏳'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-black tracking-tight">
                        {isExp ? 'Expired Products Stock Card' : 'Expiring Soon Stock Card'}
                      </h3>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-white/25 text-white border border-white/20">
                        {activePopupItems.length} {activePopupItems.length === 1 ? 'Item' : 'Items'}
                      </span>
                    </div>
                    <p className="text-xs text-white/85 font-medium mt-0.5">
                      {isExp 
                        ? 'Products that have crossed their expiry date — ready for stock return to suppliers' 
                        : 'Products expiring within the next 2 months — prepare replacements or returns'}
                    </p>
                  </div>
                </div>

                {/* Inner Tab Switcher & Close */}
                <div className="flex items-center gap-3">
                  <div className="bg-black/20 p-1 rounded-xl flex items-center gap-1 border border-white/10">
                    <button
                      type="button"
                      onClick={() => {
                        setPopupType('expired');
                        setPopupSupplierFilter('ALL');
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                        isExp ? 'bg-white text-rose-700 shadow-sm' : 'text-white/80 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      ⚠️ Expired ({expiredItems.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPopupType('expiring_soon');
                        setPopupSupplierFilter('ALL');
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                        !isExp ? 'bg-white text-amber-800 shadow-sm' : 'text-white/80 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      ⏳ Expiring Soon ({expiringSoonItems.length})
                    </button>
                  </div>

                  <button 
                    onClick={() => setPopupType(null)}
                    className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                    title="Close (Esc)"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Metrics Ribbon */}
              <div className="bg-slate-50 border-b border-slate-200/80 px-6 py-3.5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-xs">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Total Medicines</span>
                  <div className="text-base font-black text-slate-800 mt-0.5">{activePopupItems.length} products</div>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-xs">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Total Quantity</span>
                  <div className="text-base font-black text-slate-800 mt-0.5">{popupTotalQty} units</div>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-xs">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Total Stock Value</span>
                  <div className={`text-base font-black mt-0.5 ${isExp ? 'text-rose-600' : 'text-amber-600'}`}>
                    ₹{popupTotalValue.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-xs">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Suppliers / Parties</span>
                  <div className="text-base font-black text-slate-800 mt-0.5">{popupSuppliers.length} parties</div>
                </div>
              </div>

              {/* Filters & Search Toolbar */}
              <div className="px-6 py-3 bg-white border-b border-slate-200/60 flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center justify-between">
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={popupSearch}
                    onChange={(e) => setPopupSearch(e.target.value)}
                    placeholder="Search medicine name, company, batch, bill no, or supplier..."
                    className="w-full pl-9 pr-8 py-2 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-400 text-slate-700"
                  />
                  {popupSearch && (
                    <button 
                      onClick={() => setPopupSearch('')}
                      className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-bold text-slate-400 shrink-0">Supplier:</label>
                  <select
                    value={popupSupplierFilter}
                    onChange={(e) => setPopupSupplierFilter(e.target.value)}
                    className="text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-700 outline-none focus:border-indigo-500"
                  >
                    <option value="ALL">All Suppliers ({currentCategorySuppliers.length})</option>
                    {currentCategorySuppliers.map((supp, i) => (
                      <option key={i} value={supp}>{supp}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-y-auto flex-1 p-6">
                {activePopupItems.length === 0 ? (
                  <div className="text-center py-16 text-slate-400">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                      🔍
                    </div>
                    <p className="font-bold text-slate-700 text-sm">No items found matching your filters</p>
                    <p className="text-xs text-slate-400 mt-1">Try resetting the search query or supplier selection</p>
                    {(popupSearch || popupSupplierFilter !== 'ALL') && (
                      <button
                        onClick={() => {
                          setPopupSearch('');
                          setPopupSupplierFilter('ALL');
                        }}
                        className="mt-3 px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
                      >
                        Reset Filters
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-slate-100/90 text-slate-700 font-extrabold border-b border-slate-200 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="p-3 text-center">#</th>
                          <th className="p-3">Medicine & Company</th>
                          <th className="p-3">Supplier / Stockist</th>
                          <th className="p-3">Bill No & Date</th>
                          <th className="p-3">Batch</th>
                          <th className="p-3 text-center">Exp Date</th>
                          <th className="p-3 text-center">Qty</th>
                          <th className="p-3 text-right">Rate (₹)</th>
                          <th className="p-3 text-right">Amount (₹)</th>
                          <th className="p-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {activePopupItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/90 transition-colors">
                            <td className="p-3 text-center font-bold text-slate-400">{idx + 1}</td>
                            <td className="p-3">
                              <div className="font-extrabold text-slate-800 text-xs">{item.productName}</div>
                              <div className="text-[11px] text-slate-400 font-medium">{item.company}</div>
                            </td>
                            <td className="p-3">
                              <span 
                                onClick={() => {
                                  setPopupType(null);
                                  setSelectedParty(item.supplier);
                                }}
                                className="font-bold text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                                title="Click to view full stock return memo for this party"
                              >
                                {item.supplier}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="font-bold text-slate-700">#{item.invoiceNo}</div>
                              <div className="text-[11px] text-slate-400">{item.purchaseDate}</div>
                            </td>
                            <td className="p-3 font-mono font-bold text-slate-700">
                              <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[11px]">
                                {item.batch}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold inline-block border ${
                                item.isExpired 
                                  ? 'bg-rose-50 text-rose-700 border-rose-200' 
                                  : 'bg-amber-50 text-amber-800 border-amber-200'
                              }`}>
                                {item.exp} {item.isExpired ? '⚠️ Expired' : '⏳ Near Expiry'}
                              </span>
                              {item.daysDiff !== undefined && (
                                <div className="text-[10px] text-slate-400 font-medium mt-0.5">
                                  {item.isExpired 
                                    ? (item.daysDiff < 0 ? `${Math.abs(item.daysDiff)}d ago` : 'Expired')
                                    : (item.daysDiff > 0 ? `in ${item.daysDiff}d` : 'Today')}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-center font-black text-slate-800">{item.qty}</td>
                            <td className="p-3 text-right text-slate-600 font-bold">₹{item.rate.toFixed(2)}</td>
                            <td className={`p-3 text-right font-black ${isExp ? 'text-rose-600' : 'text-amber-700'}`}>
                              ₹{item.amount.toFixed(2)}
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => {
                                  setPopupType(null);
                                  setSelectedParty(item.supplier);
                                }}
                                className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-[11px] rounded-lg border border-indigo-200 transition-colors"
                                title="Open party stock return memo"
                              >
                                Return Memo
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleCopyPopupReport(popupType, activePopupItems)}
                    className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl text-xs flex items-center gap-1.5 border border-indigo-200 transition-colors cursor-pointer"
                  >
                    📋 Copy Report
                  </button>
                  <button
                    onClick={() => handlePrintPopupReport(popupType, activePopupItems)}
                    className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-xl text-xs flex items-center gap-1.5 border border-emerald-200 transition-colors cursor-pointer"
                  >
                    🖨️ Print Stock Report
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">Total Value:</span>
                    <span className={`text-base font-black ml-1.5 ${isExp ? 'text-rose-600' : 'text-amber-700'}`}>
                      ₹{popupTotalValue.toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={() => setPopupType(null)}
                    className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs shadow-sm transition-all cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Party Details & Stock Return Modal */}
      {selectedParty && (() => {
        const fullPartyItems = expiringItems.filter(item => item.supplier === selectedParty);
        const partyItems = fullPartyItems.filter(item => {
          if (partyItemsFilter === 'EXPIRED') return item.isExpired;
          if (partyItemsFilter === 'SOON') return !item.isExpired;
          return true;
        });

        const partyTotalQty = partyItems.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0);
        const partyTotalAmt = partyItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        const expiredInParty = fullPartyItems.filter(i => i.isExpired);
        const soonInParty = fullPartyItems.filter(i => !i.isExpired);
        
        return (
          <div 
            onClick={() => setSelectedParty(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200"
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
                <div>
                  <h3 className="text-xl font-black">{selectedParty}</h3>
                  <p className="text-xs text-slate-300 font-bold mt-0.5">Stock Return Memo - Near Expiry & Expired Items</p>
                </div>
                <button 
                  onClick={() => setSelectedParty(null)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors cursor-pointer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="overflow-y-auto p-6 flex-1 space-y-4">
                {/* Summary Banner */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold text-slate-400 uppercase">Total Items</span>
                    <div className="text-lg font-black text-slate-800">{partyItems.length} medicines</div>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-400 uppercase">Total Return Quantity</span>
                    <div className="text-lg font-black text-slate-800">{partyTotalQty} units</div>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-400 uppercase block mb-1">Credit Note Amount (₹)</span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-black text-teal-700">₹</span>
                      <input 
                        type="number"
                        step="0.01"
                        value={customCnAmount !== '' ? customCnAmount : partyTotalAmt.toFixed(2)}
                        onChange={(e) => setCustomCnAmount(e.target.value)}
                        className="w-28 px-2 py-1 bg-white border border-teal-300 rounded-lg text-sm font-black text-emerald-700 outline-none focus:border-teal-500 shadow-inner text-right"
                        placeholder={partyTotalAmt.toFixed(2)}
                      />
                    </div>
                  </div>
                </div>

                {/* Filter Tabs inside Party Modal */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPartyItemsFilter('ALL')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      partyItemsFilter === 'ALL'
                        ? 'bg-slate-800 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    All Items ({fullPartyItems.length})
                  </button>
                  <button
                    onClick={() => setPartyItemsFilter('EXPIRED')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      partyItemsFilter === 'EXPIRED'
                        ? 'bg-rose-600 text-white shadow-xs'
                        : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200'
                    }`}
                  >
                    Expired Only ({expiredInParty.length})
                  </button>
                  <button
                    onClick={() => setPartyItemsFilter('SOON')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      partyItemsFilter === 'SOON'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    Near Expiry Only ({soonInParty.length})
                  </button>
                </div>

                {/* Items Table */}
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                      <tr>
                        <th className="p-3">Bill No & Date</th>
                        <th className="p-3">Product Name</th>
                        <th className="p-3">Batch</th>
                        <th className="p-3 text-center">Exp Date</th>
                        <th className="p-3 text-center">Qty</th>
                        <th className="p-3 text-right">Rate</th>
                        <th className="p-3 text-right">Return Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium">
                      {partyItems.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80">
                          <td className="p-3">
                            <div className="font-bold text-slate-800">#{item.invoiceNo}</div>
                            <div className="text-[11px] text-slate-500">{item.purchaseDate}</div>
                          </td>
                          <td className="p-3">
                            <div className="font-bold text-slate-800">{item.productName}</div>
                            <div className="text-[11px] text-slate-500">{item.company}</div>
                          </td>
                          <td className="p-3 font-mono font-bold text-slate-700">{item.batch}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2.5 py-1 rounded-md text-xs font-bold inline-block ${
                              item.isExpired 
                                ? 'bg-rose-100 text-rose-700 border border-rose-200' 
                                : 'bg-amber-100 text-amber-800 border border-amber-200'
                            }`}>
                              {item.exp} {item.isExpired ? '(Expired)' : ''}
                            </span>
                          </td>
                          <td className="p-3 text-center font-black text-slate-800">{item.qty}</td>
                          <td className="p-3 text-right text-slate-600">₹{item.rate.toFixed(2)}</td>
                          <td className="p-3 text-right font-black text-emerald-600">₹{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleCopyReturnList(selectedParty, partyItems)}
                    className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl transition-colors text-xs flex items-center gap-1.5 border border-indigo-200 cursor-pointer"
                  >
                    📋 Copy Details
                  </button>
                  <button 
                    onClick={() => handlePrintReturnNote(selectedParty, partyItems)}
                    className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-xl transition-colors text-xs flex items-center gap-1.5 border border-emerald-200 cursor-pointer"
                  >
                    🖨️ Print Return Note
                  </button>
                  <button 
                    disabled={isCreatingCN}
                    onClick={() => handleCreateCreditNote(selectedParty, partyItems, customCnAmount !== '' ? customCnAmount : partyTotalAmt)}
                    className="px-5 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-black rounded-xl transition-all shadow-md text-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <span>💳 {isCreatingCN ? 'Creating...' : `Create Credit Note (₹${(customCnAmount !== '' ? parseFloat(customCnAmount) || 0 : partyTotalAmt).toFixed(2)})`}</span>
                  </button>
                </div>
                
                <button 
                  onClick={() => setSelectedParty(null)}
                  className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-colors shadow-sm text-xs cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Credit Note Success & Confirmation Modal */}
      {showCNSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white border-2 border-emerald-500 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 bg-gradient-to-r from-emerald-600 to-teal-700 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 text-3xl">
                💳
              </div>
              <h3 className="text-xl font-black">Credit Note Created!</h3>
              <p className="text-xs text-emerald-100 font-bold mt-1">Stock return successfully credited to supplier ledger</p>
            </div>

            <div className="p-6 space-y-4 text-xs font-bold text-slate-700 bg-white">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-2">
                <div className="flex justify-between border-b border-emerald-200/60 pb-2">
                  <span className="text-slate-500">Credit Note No:</span>
                  <span className="font-black text-emerald-800 text-sm">{showCNSuccessModal.creditNoteNo}</span>
                </div>
                <div className="flex justify-between border-b border-emerald-200/60 pb-2">
                  <span className="text-slate-500">Supplier / Party:</span>
                  <span className="font-black text-slate-800">{showCNSuccessModal.supplierName}</span>
                </div>
                <div className="flex justify-between border-b border-emerald-200/60 pb-2">
                  <span className="text-slate-500">Return Date:</span>
                  <span className="font-black text-slate-800">{showCNSuccessModal.date}</span>
                </div>
                <div className="flex justify-between border-b border-emerald-200/60 pb-2">
                  <span className="text-slate-500">Returned Items & Qty:</span>
                  <span className="font-black text-slate-800">{showCNSuccessModal.totalItems} Items ({showCNSuccessModal.totalQty} Units)</span>
                </div>
                <div className="flex justify-between pt-1 text-emerald-900 text-sm font-black">
                  <span>Credited to Party Ledger:</span>
                  <span className="text-base text-emerald-600">₹{parseFloat(showCNSuccessModal.totalAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-500 leading-relaxed font-bold">
                ✨ This returned value of <strong className="text-emerald-700">₹{parseFloat(showCNSuccessModal.totalAmount || 0).toFixed(2)}</strong> has been posted as a Credit Note in <strong>Supplier Ledger & Stockist Statements</strong>.
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-100 border-t border-slate-200 flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowCNSuccessModal(null);
                  setSelectedParty(null);
                }}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md transition-all cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NearExpiry;
